import * as G6 from '@antv/g6';
import { ComboConfig, TreeGraphData } from '@antv/g6';
import { PrivateGraphOption } from '@antv/g6-core/lib/graph/graph';
import { EdgeConfig, NodeConfig } from '@antv/g6-core/lib/types';
import workerScriptURL from '@antv/layout/dist/layout.min.js?url';
import { Color } from '@persona-id/design-tokens';
import { throttle } from 'lodash/fp';

import {
  MAX_ZOOM,
  MIN_ZOOM,
} from 'dashboard/components/GraphCanvasV2/behaviors/camera-select-behavior';
import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import GraphDataChangeEvent from 'dashboard/components/GraphCanvasV2/events/graph-data-change-event';
import {
  canvasSelectableToG6Id,
  collapsedGroupIdToG6Id,
  collapsedPairwiseNodeIdToG6Id,
  g6IdToCollapsedGroupId,
  g6IdToCollapsedPairwiseNodeId,
  g6IdToNodeId,
  hashableNodeIdToG6Id,
  nodeIdToG6Id,
} from 'dashboard/components/GraphCanvasV2/G6Mapper';
import registerDataNodes from 'dashboard/components/GraphCanvasV2/nodes-g6/register-data-nodes';
import Grid from 'dashboard/components/GraphCanvasV2/plugins-g6/grid';
import { getDebugMode, NODE_ICON_SOURCES } from 'dashboard/lib/utils/graph';
import {
  CanvasSelectable,
  CollapsedGroup,
  CollapsedGroupId,
  CollapsedPairwiseNodeId,
  G6GraphUpdateMessage,
  G6Node,
  G6NodeId,
  G6SetGraphMessage,
  GraphNode,
  HashableNodeId,
  Node as GraphModelNode,
  NodeId,
  nodeIdToHashable,
} from 'dashboard/models/graph';
import { GraphExplorer } from 'dashboard/models/graph-explorer';
import { ACCOUNT_DATUM_TYPE } from 'lib/constants/graph';

export interface NodePosition {
  x: number;
  y: number;
}

const ANIMATION_DURATION_MS = 1000;
const IDEAL_EDGE_LENGTH = 50;
const EXPANSION_RADIUS = IDEAL_EDGE_LENGTH;

const getWorkerUrl = (): string => {
  try {
    new URL(workerScriptURL);
    return workerScriptURL;
  } catch (error) {
    return new URL(workerScriptURL, import.meta.url).href;
  }
};

const computeExpandNodePositions = (
  position: NodePosition,
  newNodeCount: number,
): NodePosition[] => {
  return Array.from(Array(newNodeCount).keys()).map((index: number) => {
    return {
      x: position.x + EXPANSION_RADIUS * Math.cos((2 * Math.PI * index) / newNodeCount),
      y: position.y + EXPANSION_RADIUS * Math.sin((2 * Math.PI * index) / newNodeCount),
    };
  });
};

type G6NodeModel = NodeConfig | EdgeConfig | ComboConfig | TreeGraphData;

type G6LayoutInfo = {
  type: string;
  maxIteration: number;
  workerEnabled: boolean;
  workerScriptURL: string;
  gpuEnabled: boolean;
  preventOverlap: boolean;
  prune: boolean;
  linkDistance: number;
  edgeStrength: number;
  minMovement: number;
  maxSpeed: number;
  coulombDisScale: number;
  damping: number;
};

declare global {
  interface Window {
    graphStateMethods: {
      getCanvas: () => HTMLCanvasElement;
      getCollapsedG6Members: (collapsedNodeId: G6NodeId) => Set<HashableNodeId>;
      getCollapsedG6Nodes: () => G6NodeModel[];
      getDefaultCfg: () => Partial<PrivateGraphOption>;
      getG6NodesAndEdges: () => {
        nodes: G6NodeModel[];
        edges: G6NodeModel[];
      };
      getHighlightedG6Nodes: () => G6NodeModel[];
      getLayoutInfo: () => G6LayoutInfo;
      getSelectedG6Nodes: () => G6NodeModel[];
      getViewState: () => { zoom: number; pan: number | Object; width: number; height: number };
      graph: G6.Graph;
    };
  }
}

class G6Wrapper {
  // ---------------------------------------------------------------------------------------
  // State machine management.
  // ---------------------------------------------------------------------------------------
  public isMounted: boolean;

  // ---------------------------------------------------------------------------------------
  // G6 configuration logic.
  // ---------------------------------------------------------------------------------------
  private initialized: boolean;

  // ---------------------------------------------------------------------------------------
  // Graph rendering and data management structures.
  // ---------------------------------------------------------------------------------------
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private g6: G6.Graph;
  private g6Ready: boolean = false;
  private readyCallbacks: (() => void)[] = [];

  eventBus: EventBus;

  // Callbacks.
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private handleNodeClick: (nodeId: NodeId) => void;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private handleExpandUnlessDimmed: (nodeIds: NodeId[]) => void;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private handleZoomPercentageChange: (zoomPercentage: number) => void;
  private handleLayoutComplete: () => void = () => undefined;
  private handleCollapsedGroupExpand: (groupId: CollapsedGroupId) => void = () => undefined;
  private handleCollapsedNodeExpand: (nodeId: CollapsedPairwiseNodeId) => void = () => undefined;

  // Selection
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  public brushRect: G6.IShape;
  private brushStyle = {
    fill: Color.CorePurple500,
    fillOpacity: 0.2,
    stroke: Color.CorePurple600,
    lineWidth: 1,
  };

  // Toolbar - we keep this around for undo/redo stack but we can consider building our own stack
  // TS error is ignored because the type definition doesn't include this class even though it does exist
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private toolbar: G6.ToolBar;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private grid: Grid;

  // ---------------------------------------------------------------------------------------
  // Analytics.
  // ---------------------------------------------------------------------------------------
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private lastDragendTimestamp: number | null;

  selecteeTargets: Record<string, G6.IShape> = {};
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  hoverCardTimeoutId: number;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  pinHoverCard: boolean;

  private _window: Window;

  constructor(eventBus: EventBus, _window: Window = window) {
    this.isMounted = false;
    this.eventBus = eventBus;
    this.initialized = false;
    this._window = _window;
  }

  // Sets up helper methods on the window object for use in Cypress tests, facilitating interaction with the G6 graph instance.
  setupCypressHelpers() {
    // Expose helper methods under 'graphStateMethods' on the window object to be accessible in Cypress tests.
    this._window.graphStateMethods = {
      // Direct reference to the graph instance for potential direct manipulations or inspections.
      graph: this.g6,
      // Returns the canvas element used by the G6 graph. Useful for testing canvas-based interactions.
      getCanvas: () => {
        return this.canvas;
      },
      // Returns the default configuration of the graph. Useful for assertions against default settings.
      getDefaultCfg: () => this.g6.getDefaultCfg(),
      // Retrieves all nodes and edges currently in the graph, formatted as their model data.
      getG6NodesAndEdges: () => {
        const nodes = this.g6.getNodes();
        const edges = this.g6.getEdges();
        return {
          nodes: nodes ? nodes.map((node) => node.getModel()) : [],
          edges: edges ? edges.map((edge) => edge.getModel()) : [],
        };
      },
      // Gets the current view state of the graph including zoom level and viewport dimensions.
      // Pan information is currently a placeholder and needs actual implementation.
      getViewState: () => ({
        zoom: this.g6.getZoom(),
        pan: { x: 0, y: 0 }, // Placeholder; should be replaced with actual pan retrieval logic.
        width: this.g6.getWidth(),
        height: this.g6.getHeight(),
      }),
      // Retrieves all nodes that are currently selected in the graph.
      // Useful for tests verifying node selection logic.
      getSelectedG6Nodes: () => {
        const nodes = this.g6.getNodes();
        return nodes
          ? nodes
              .filter((node) => {
                const model = node.getModel();
                // Filter nodes by their 'selected' state. The path to the 'selected' state may need adjustments.
                // @ts-expect-error [strict-migration] The following line is not strict; please fix!
                return model.dataNode?.state?.selected;
              })
              .map((node) => node.getModel())
          : [];
      },
      // Retrieves nodes representing collapsed group nodes.
      // Useful for testing functionality related to node grouping and collapse/expand behavior.
      getCollapsedG6Nodes: () => {
        const nodes = this.g6.getNodes();
        return nodes
          ? nodes
              .filter((node) => node.getModel().type === 'graph_collapsed_group')
              .map((node) => node.getModel())
          : [];
      },
      // Retrieves the current layout configuration of the graph.
      // This can be important for tests that depend on the graph's visual arrangement.
      getLayoutInfo: () => this.g6.get('layout'),
      // Retrieves nodes that are currently highlighted.
      // This is useful for testing features related to node highlighting.
      getHighlightedG6Nodes: () => {
        const nodes = this.g6.getNodes();
        return nodes
          ? nodes
              // @ts-expect-error [strict-migration] The following line is not strict; please fix!
              .filter((node) => node.getModel().dataNode?.state.highlighted)
              .map((node) => node.getModel())
          : [];
      },
      // Given the ID of a collapsed group node, returns the members of that group.
      // This is useful for verifying the contents of a group after manipulation.
      getCollapsedG6Members: (collapsedNodeId) => {
        const node = this.g6.findById(collapsedNodeId);
        return node ? node.get('model').dataNode?.members : new Set();
      },
    };
  }

  public get window(): Window {
    return this._window;
  }

  isInitialized() {
    return this.initialized;
  }

  initialize(graphExplorer: GraphExplorer) {
    // Configure G6 to be able to handle our custom nodes, if we haven't already.
    if (!this.initialized) {
      // Register Nodes
      registerDataNodes(G6, (token) => {
        return NODE_ICON_SOURCES[graphExplorer.getNodeRenderConfig(token).icon];
      });
      this.initialized = true;
    }
  }

  private getLayoutCfg(numNodes?: number) {
    return {
      // We use GForce, because Force Atlas 2 doesn't work well with adding new nodes.
      type: 'gForce',
      maxIteration: 1000,
      workerEnabled: true,
      // We have to specify this if workerEnabled is true, otherwise the dynamic import from unpkg.com will work
      // locally but fail on staging and prod
      workerScriptURL: getWorkerUrl(),
      // For large graphs, GPU parallel computing has substantially better performance
      // https://antv-g6.gitee.io/en/docs/api/graphLayout/gforce/#layoutcfggpuenabled
      gpuEnabled: numNodes && numNodes > 500 ? true : false,
      preventOverlap: true,
      // Disable pruning. Normally, pruning is auto-enabled if there are more than 100 nodes to render.
      // Pruning means the layout engine ignores leaf nodes when iterating, except for the last 50 iterations.
      // If you change this, test with many (e.g. 100) nodes connected to a single node, and more than 100 nodes in total throughout the graph.
      prune: false,
      // Set node strength so that nodes repel each other. According to the docs,
      // positive means repulsion and negative means attraction.
      // account nodes should have a higher replusion force so that they don't attract too close to each other
      nodeStrength: (d: GraphNode) => {
        return d.type === `Graph_${ACCOUNT_DATUM_TYPE.token}` ? 1500 : 1000;
      },
      // Edge length
      linkDistance: IDEAL_EDGE_LENGTH,
      // Set edge strength so that nodes along an edge attract each other. According to the (Chinese) docs, positive means
      // attraction.
      edgeStrength: 200,
      // When the average movement of nodes in one iteration is smaller than minMovement, terminate the layout
      minMovement: 0.5,
      // The maximum speed that nodes can move.
      maxSpeed: 1000,
      // How much nodes repel each other.
      coulombDisScale: 0.0075,
      // How much to ease the animation over successive iterations.
      damping: 0.9,
      onLayoutEnd: () => {
        this.handleLayoutComplete();
      },
    };
  }

  bindToContainer(container: HTMLDivElement, width: number, height: number) {
    this.toolbar = new G6.ToolBar({
      getContent() {
        // Overwrite default rendering. We use React to render our own toolbar
        // but keep this plugin around for access to it's undo/redo stack.
        return '<div style="display: none"/>';
      },
    });

    this.grid = new Grid();

    this.g6 = new G6.Graph({
      width: width,
      height: height,
      animate: true,
      animateCfg: {
        duration: ANIMATION_DURATION_MS,
        easing: 'easePolyOut',
      },
      autoPaint: true,
      fitView: true,
      fitViewPadding: 30,
      linkCenter: true,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      enabledStack: true,
      plugins: [this.toolbar, this.grid],
      defaultEdge: {
        type: 'line',
        style: {
          stroke: Color.CoreSlate200,
          lineWidth: 3,
          strokeOpacity: 0.7,
        },
      },
      layout: this.getLayoutCfg(),
      container: container,
    });

    this.g6Ready = true;

    this.readyCallbacks.forEach((callback) => callback());
    this.readyCallbacks = [];

    // Let the G6 instance reference this wrapper and it's functionality
    // (important if we want to incorporate wrapper functionality within
    // custom G6 behavior)
    //
    // NOTE: There is possibly a way to avoid this if we define custom behaviors
    // and nodes within G6Wrapper and pass it into G6.
    //
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.g6.wrapper = this;

    this.g6.on('node:click', (ev) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (ev.item.destroyed) {
        // Don't do anything on destroyed nodes
        return;
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const node: G6Node = ev.item.get('model');
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.handleNodeClick?.(g6IdToNodeId(node.id));

      if (getDebugMode()) {
        /* eslint-disable no-console */
        console.group('%c' + `Node: ${node.id}`, 'font-weight:bold');
        Object.keys(node).forEach((key) => {
          if (key !== 'id') {
            console.log(key, ':', node[key]);
          }
        });
        console.groupEnd();
        /* eslint-enable no-console */
      }
    });

    this.g6.on('node:dblclick', (ev) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const g6Id = ev.item.get('model').id;

      // only expand nodes and not collapsed groups
      const nodeId = g6IdToNodeId(g6Id);
      if (this.handleExpandUnlessDimmed && nodeId) {
        this.handleExpandUnlessDimmed([nodeId]);
      }

      const collapsedGroupId = g6IdToCollapsedGroupId(g6Id);
      if (this.handleCollapsedGroupExpand && collapsedGroupId) {
        this.handleCollapsedGroupExpand(collapsedGroupId);
      }

      const collapsedNodeId = g6IdToCollapsedPairwiseNodeId(g6Id);
      if (this.handleCollapsedNodeExpand && collapsedNodeId) {
        this.handleCollapsedNodeExpand(collapsedNodeId);
      }
    });

    this.g6.on('canvas:click', (_ev) => {
      this.pinHoverCard = false;
    });

    this.g6.on('dragend', (ev) => {
      if (this.lastDragendTimestamp === ev.timeStamp) {
        // This event fires twice
        // Ignore any events with the same timestamp as the previously-logged event
        return;
      }
      this.lastDragendTimestamp = ev.timeStamp;
    });

    this.g6.on('viewportchange', (ev: G6.ViewPortEventParam) => {
      if (ev.action === 'zoom' && typeof this.handleZoomPercentageChange === 'function') {
        this.handleZoomPercentageChange(Math.round(ev.matrix[0] * 100));
      }
    });

    if (this._window.Cypress) {
      this.setupCypressHelpers();
    }

    this.isMounted = true;
  }

  destroy() {
    this.g6.destroy();
  }

  get destroyed() {
    return this.g6.destroyed;
  }

  get canvasEl() {
    return this.canvas.get('el');
  }

  get canvas() {
    return this.graph.get('canvas');
  }

  get graph() {
    return this.g6;
  }

  findNodesAtPosition(x: number, y: number) {
    return (this.g6.getNodes() || []).filter((node) => {
      const nodeBBox = node.getBBox();
      // be a bit generous to ensure that the node is completely within its bbox
      const epsilon = 2;
      return (
        nodeBBox.minY <= y + epsilon &&
        y - epsilon <= nodeBBox.maxY &&
        nodeBBox.minX <= x + epsilon &&
        x - epsilon <= nodeBBox.maxX
      );
    });
  }

  findEdgesAtPosition(x: number, y: number) {
    return (this.g6.getEdges() || []).filter((edge) => {
      // first check if coord is within the edge BBox
      const edgeBBox = edge.getBBox();
      if (edgeBBox.minY > y || y > edgeBBox.maxY || edgeBBox.minX > x || x > edgeBBox.maxX) {
        return false;
      }

      // get the position of the edge's 2 adjacent nodes. then check if the distance between
      // the coord and the line-segment connecting the two nodes is within some threshold
      const { centerX: x1, centerY: y1 } = edge.getSource().getBBox();
      const { centerX: x2, centerY: y2 } = edge.getTarget().getBBox();

      const square = (x: number) => x * x;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const denominator = Math.sqrt(square(x2 - x1) + square(y2 - y1));
      if (denominator === 0) {
        return false;
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const distToLine = Math.abs((x2 - x1) * (y1 - y) - (y2 - y1) * (x1 - x)) / denominator;
      // TODO (bjon): thickness dependent on zoom, pretty difficult to get the actual edge thickness
      // hardcoding this value works pretty well for the time being, but should change
      const thickness = 3;

      return distToLine < thickness;
    });
  }

  nodeByNodeId(nodeId: NodeId) {
    return this.g6.findById(nodeIdToG6Id(nodeId)) as G6.INode;
  }

  nodeByCollapsedGroupId(groupId: CollapsedGroupId) {
    return this.g6.findById(collapsedGroupIdToG6Id(groupId)) as G6.INode;
  }

  nodeByCollapsedPairwiseNodeId(nodeId: CollapsedPairwiseNodeId) {
    return this.g6.findById(collapsedPairwiseNodeIdToG6Id(nodeId)) as G6.INode;
  }

  nodeBBoxByNodeId(nodeId: NodeId) {
    return this.nodeByNodeId(nodeId).getBBox();
  }

  nodeBySelectable(selectable: CanvasSelectable) {
    return this.g6.findById(canvasSelectableToG6Id(selectable));
  }

  nodeBBoxBySelectable(selectable: CanvasSelectable) {
    return this.nodeBySelectable(selectable).getBBox();
  }

  setNodePosition(nodeId: NodeId, x: number, y: number) {
    const node = this.g6.findById(nodeIdToG6Id(nodeId));
    this.g6.updateItem(node, { x, y });
  }

  setNodePositions(newPositions: Map<HashableNodeId, NodePosition>) {
    for (const [hashableId, { x, y }] of newPositions) {
      const node = this.g6.findById(hashableNodeIdToG6Id(hashableId));
      this.g6.updateItem(node, { x, y });
    }
  }

  setCollapsedGroupPosition(collapsedGroupId: CollapsedGroupId, x: number, y: number) {
    const node = this.g6.findById(collapsedGroupIdToG6Id(collapsedGroupId));
    this.g6.updateItem(node, { x, y });
  }

  setCollapsedGroupPositions(newPositions: Map<CollapsedGroupId, NodePosition>) {
    for (const [collapsedGroupId, { x, y }] of newPositions) {
      const node = this.g6.findById(collapsedGroupIdToG6Id(collapsedGroupId));
      this.g6.updateItem(node, { x, y });
    }
  }

  setCollapsedPairwiseNodePosition(collapsedNodeId: CollapsedPairwiseNodeId, x: number, y: number) {
    const node = this.g6.findById(collapsedPairwiseNodeIdToG6Id(collapsedNodeId));
    this.g6.updateItem(node, { x, y });
  }

  setCollapsedPairwiseNodePositions(newPositions: Map<CollapsedPairwiseNodeId, NodePosition>) {
    for (const [collapsedPairwiseNodeId, { x, y }] of newPositions) {
      const node = this.g6.findById(collapsedPairwiseNodeIdToG6Id(collapsedPairwiseNodeId));
      this.g6.updateItem(node, { x, y });
    }
  }

  // Can be updated after G6 is mounted.
  setHandleNodeClick(handleNodeClick: (nodeId: NodeId) => void) {
    this.handleNodeClick = handleNodeClick;
  }

  setHandleOneHopExpansion(handleExpandUnlessDimmed: (nodeIds: NodeId[]) => void) {
    this.handleExpandUnlessDimmed = handleExpandUnlessDimmed;
  }

  setHandleCollapsedGroupExpand(handleCollapsedGroupExpand: (groupId: CollapsedGroupId) => void) {
    this.handleCollapsedGroupExpand = handleCollapsedGroupExpand;
  }

  setHandleCollapsedNodeExpand(
    handleCollapsedNodeExpand: (nodeId: CollapsedPairwiseNodeId) => void,
  ) {
    this.handleCollapsedNodeExpand = handleCollapsedNodeExpand;
  }

  setHandleZoomPercentageChange(handleZoomPercentageChange: (zoomPercentage: number) => void) {
    this.handleZoomPercentageChange = handleZoomPercentageChange;
  }

  setHandleLayoutComplete(handleLayoutComplete: () => void) {
    this.handleLayoutComplete = handleLayoutComplete;
  }

  abortLayout() {
    this.g6.updateLayout({ ...this.getLayoutCfg(), maxIteration: 1 });
  }

  changeSize(width: number, height: number) {
    if (this.g6?.get('viewController')) {
      this.g6.changeSize(width, height);
    }
  }

  // we expect all data g6Nodes to already be given positions.
  // We do not trigger any layout algorithm.
  setDataWithPositions(message: G6SetGraphMessage) {
    if (!this.isMounted) {
      throw new Error('Attempted to set data before canvas is mounted');
    }

    this.g6.updateLayout({ ...this.getLayoutCfg(), maxIteration: 0 });
    this.g6.read({ nodes: message.nodes, edges: message.edges });

    // manually center the canvas such that all nodes are in view
    // this.g6.set('fitView', true); does not actually recenter it... (it is offcentered by a bit)
    this.g6.set('fitView', false);
    if (message.nodes.length > 0) {
      let maxX;
      let minX;
      let maxY;
      let minY;
      for (const g6Node of this.g6.getNodes()) {
        const { maxX: bboxMaxX, minX: bboxMinX, maxY: bboxMaxY, minY: bboxMinY } = g6Node.getBBox();
        maxX = maxX ? Math.max(bboxMaxX, maxX) : bboxMaxX;
        minX = minX ? Math.min(bboxMinX, minX) : bboxMinX;
        maxY = maxY ? Math.max(bboxMaxY, maxY) : bboxMaxY;
        minY = minY ? Math.min(bboxMinY, minY) : bboxMinY;
      }
      const padding = 40;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const zoomX = this.graph.getWidth() / (maxX - minX + padding);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const zoomY = this.graph.getHeight() / (maxY - minY + padding);

      this.graph.zoomTo(Math.min(zoomX, zoomY), { x: 0, y: 0 });
      this.graph.translate(this.graph.getWidth() / 2, this.graph.getHeight() / 2);
      this.zoomSetPercentage(this.zoomPercentage());
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.eventBus.dispatchEvent(new GraphDataChangeEvent(null, this.canvas));
  }

  setData(message: G6SetGraphMessage) {
    if (!this.isMounted) {
      throw new Error('Attempted to set data before canvas is mounted');
    }
    this.g6.updateLayout(this.getLayoutCfg(message.nodes.length));
    // We set `fitView: true` here so that we recenter upon the initial load.
    this.g6.set('fitView', true);
    this.g6.read({ nodes: message.nodes, edges: message.edges });
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.eventBus.dispatchEvent(new GraphDataChangeEvent(null, this.canvas));
  }

  // ---------------------------------------------------------------------------------------
  // Emitter
  // ---------------------------------------------------------------------------------------
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emitG6Event(...args: any[]) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.g6.emit(...args);
  }

  // ---------------------------------------------------------------------------------------
  // Graph Undo / Redo.
  // ---------------------------------------------------------------------------------------
  inspectUndoStack() {
    return JSON.stringify(this.g6.getUndoStack());
  }

  inspectRedoStack() {
    return JSON.stringify(this.g6.getRedoStack());
  }

  // ---------------------------------------------------------------------------------------
  // Graph node/edge add/update/delete operations.
  // ---------------------------------------------------------------------------------------

  private ensureG6Ready(callback: () => void) {
    if (this.g6Ready) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  updateGraph(message: G6GraphUpdateMessage) {
    this.ensureG6Ready(() => {
      message.items.forEach((item) => {
        switch (item.messageType) {
          case 'add_node':
            this.g6.addItem('node', item.node);
            break;
          case 'add_collapsed_group':
            this.g6.addItem('node', item.collapsedGroup);
            break;
          case 'add_collapsed_pairwise_node':
            this.g6.addItem('node', item.node);
            break;
          case 'update_collapsed_pairwise_node':
            this.g6.updateItem(item.node.id, item.node);
            break;
          case 'remove_collapsed_pairwise_node':
            this.g6.removeItem(item.node.id);
            break;
          case 'update_node':
            this.g6.updateItem(item.node.id, item.node);
            break;
          case 'update_collapsed_group':
            this.g6.updateItem(item.collapsedGroup.id, item.collapsedGroup);
            break;
          case 'remove_node':
            this.g6.removeItem(item.id);
            break;
          case 'remove_collapsed_group':
            this.g6.removeItem(item.id);
            break;
          case 'add_edge':
            this.g6.addItem('edge', item.edge);
            break;
          case 'update_edge':
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.g6.updateItem(item.edge.id, item.edge);
            break;
          case 'remove_edge':
            this.g6.removeItem(item.edgeId);
            break;
        }
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.eventBus.dispatchEvent(new GraphDataChangeEvent(null, this.canvas));
    });
  }

  relayout() {
    // Important: `g6.cfg.data` does NOT get updated from the `g6.addItem` calls above, so
    //            we call `save()` and reload the data to ensure that it gets reflected in
    //            `g6.cfg.data`, where it is read into the layout renderer.
    const data = this.g6.save();
    // We set `fitView: false` here, so that we don't do a janky viewport recentering upon
    // the layout refresh.
    this.g6.set('fitView', false);
    // We grab the center coordinates before and after G6's (re)layout, because G6 will
    // recenter the viewport upon reading and rendering the new data.
    const { x: oldCenterX, y: oldCenterY } = this.g6.getGraphCenterPoint();
    // We use .data() instead of .read() because we want to re-specify the layout cfg before render()
    this.g6.data(data);
    this.g6.updateLayout(this.getLayoutCfg((data.nodes as NodeConfig[]).length));
    const { x: newCenterX, y: newCenterY } = this.g6.getGraphCenterPoint();
    // Do some vector math to restore the old viewport.
    this.g6.translate(oldCenterX - newCenterX, oldCenterY - newCenterY);

    this.zoomSetPercentage(this.zoomPercentage());
  }

  // ---------------------------------------------------------------------------------------
  // Zoom
  // ---------------------------------------------------------------------------------------
  zoomSetPercentage(zoomPercentage: number) {
    return this.zoomSet(zoomPercentage / 100);
  }

  zoomPercentage() {
    return Math.round(this.g6.getZoom() * 100);
  }

  get zoom() {
    return this.g6.getZoom();
  }

  private zoomSet(zoomRatio: number) {
    if (!this.g6) return;

    this.g6.zoomTo(zoomRatio, this.zoomCenterPoint());
  }

  // NOTE: zoomCenterPoint should be the cursor if the cursor is on the canvas.
  // but this function does not handle that since mouse positions are sent along
  // with the mouse events.
  private zoomCenterPoint(): { x: number; y: number } {
    return this.g6.getGraphCenterPoint();
  }

  // ---------------------------------------------------------------------------------------
  // Undo/Redo
  // ---------------------------------------------------------------------------------------
  undo() {
    if (!this.toolbar) return;

    this.toolbar.undo();
  }

  redo() {
    if (!this.toolbar) return;

    this.toolbar.redo();
  }

  // ---------------------------------------------------------------------------------------
  // Node expansion.
  // ---------------------------------------------------------------------------------------

  getExpansionNodePositions(nodeId: NodeId, newNodeCount: number) {
    const node = this.g6.findById(nodeIdToG6Id(nodeId));
    const nodeBoundingBox = node.getBBox();
    const position: NodePosition = {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      x: nodeBoundingBox.centerX,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      y: nodeBoundingBox.centerY,
    };
    return computeExpandNodePositions(position, newNodeCount);
  }

  // ---------------------------------------------------------------------------------------
  // Progressive loading.
  // ---------------------------------------------------------------------------------------

  getProgressiveLoadingNodePositions(newNodeCount: number): NodePosition[] {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const minX = Math.min(...this.g6.getNodes().map((node) => node.getModel().x));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const minY = Math.min(...this.g6.getNodes().map((node) => node.getModel().y));

    const initialRadius = (IDEAL_EDGE_LENGTH * newNodeCount) / 2 / Math.PI;

    return [...Array(newNodeCount).keys()].map((_, index) => {
      return {
        x: minX - initialRadius + initialRadius * Math.cos((2 * Math.PI * index) / newNodeCount),
        y: minY - initialRadius + initialRadius * Math.sin((2 * Math.PI * index) / newNodeCount),
      };
    });
  }

  // ---------------------------------------------------------------------------------------
  // Experimental Live
  // ---------------------------------------------------------------------------------------

  getExperimentalLiveNodePositions(newNodes: GraphModelNode[], edges: any[]): NodePosition[] {
    const newNodeCount = newNodes.length;

    const targets = edges.flatMap((edge) => [nodeIdToG6Id(edge.id1), nodeIdToG6Id(edge.id2)]);
    const existingG6Node = this.g6.getNodes().find((g6Node) => {
      return targets.includes(g6Node.getID());
    });

    const initialRadius = (IDEAL_EDGE_LENGTH * newNodeCount) / 2 / Math.PI;
    let startX: number;
    let startY: number;
    if (existingG6Node == null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const minX = Math.min(...this.g6.getNodes().map((node) => node.getModel().x));
      const maxX = Math.max(
        ...(this.g6.getNodes().map((node) => node.getModel().maxX) as number[]),
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const minY = Math.min(...this.g6.getNodes().map((node) => node.getModel().y));
      const maxY = Math.max(
        ...(this.g6.getNodes().map((node) => node.getModel().maxY) as number[]),
      );

      const rand = Math.random();
      if (rand <= 0.125) {
        // top left
        startX = minX;
        startY = minY;
      } else if (0.125 < rand && rand <= 0.25) {
        // bottom left
        startX = minX;
        startY = maxY;
      } else if (0.25 < rand && rand <= 0.375) {
        // top right
        startX = maxX;
        startY = minY;
      } else if (0.375 < rand && rand <= 0.5) {
        // bottom right
        startX = maxX;
        startY = maxY;
      } else if (0.5 < rand && rand <= 0.625) {
        // top
        startX = (maxX + minX) / 2;
        startY = minY;
      } else if (0.625 < rand && rand <= 0.75) {
        // right
        startX = maxX;
        startY = (minY + maxY) / 2;
      } else if (0.75 < rand && rand <= 0.875) {
        // bottom
        startX = (maxX + minX) / 2;
        startY = maxY;
      } else {
        // left
        startX = minX;
        startY = (minY + maxY) / 2;
      }
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startX = existingG6Node.getBBox().centerX;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startY = existingG6Node.getBBox().centerY;
    }

    return [...Array(newNodeCount).keys()].map((_, index) => {
      return {
        x: startX - initialRadius + initialRadius * Math.cos((2 * Math.PI * index) / newNodeCount),
        y: startY - initialRadius + initialRadius * Math.sin((2 * Math.PI * index) / newNodeCount),
      };
    });
  }

  // ---------------------------------------------------------------------------------------
  // Point conversions
  // ---------------------------------------------------------------------------------------

  getCanvasByPoint(x: number, y: number): { x: number; y: number } {
    return this.g6.getCanvasByPoint(x, y);
  }

  getCanvasByClient(x: number, y: number): { x: number; y: number } {
    const point = this.g6.getPointByClient(x, y);
    return this.g6.getCanvasByPoint(point.x, point.y);
  }

  getClientByPoint(x: number, y: number): { x: number; y: number } {
    return this.g6.getClientByPoint(x, y);
  }

  getPointByClient(x: number, y: number): { x: number; y: number } {
    return this.g6.getPointByClient(x, y);
  }

  getPointByCanvas(x: number, y: number): { x: number; y: number } {
    return this.g6.getPointByCanvas(x, y);
  }

  // ---------------------------------------------------------------------------------------
  // Brush rectangle creation
  // ---------------------------------------------------------------------------------------
  get isBrushActive() {
    return this.brushRect != null;
  }

  // Takes world coordinates
  getNodesInBox(minX: number, minY: number, maxX: number, maxY: number): G6.INode[] {
    return this.g6.getNodes().filter((g6Node) => {
      if (g6Node.getID() === 'defaultSelectionBox') {
        return false;
      }

      const nodeBBox = g6Node.getBBox();
      // Check if the box overlaps with any of the nodes
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        minX < nodeBBox.centerX &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        nodeBBox.centerX < maxX &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        minY < nodeBBox.centerY &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        nodeBBox.centerY < maxY
      );
    });
  }

  brushStart({ width, height, x, y }: { width: number; height: number; x: number; y: number }) {
    this.brushRect = this.g6.get('canvas').addShape('rect', {
      attrs: this.brushStyle,
      capture: false,
      name: 'brush-shape',
    });

    this.brushRect.attr({
      width,
      height,
      x,
      y,
    });

    return this.brushRect;
  }

  brushUpdate({ width, height, x, y }: { width: number; height: number; x: number; y: number }) {
    this.brushRect.attr({
      width,
      height,
      x,
      y,
    });

    this.throttledNodeSelecteeTargetUpdate();
  }

  throttledNodeSelecteeTargetUpdate = throttle(16, () => {
    if (!this.brushRect) {
      return;
    }

    const {
      minX: brushMinXCanvas,
      minY: brushMinYCanvas,
      maxX: brushMaxXCanvas,
      maxY: brushMaxYCanvas,
    } = this.brushRect.getBBox();

    const { x: brushMinX, y: brushMinY } = this.getPointByCanvas(brushMinXCanvas, brushMinYCanvas);
    const { x: brushMaxX, y: brushMaxY } = this.getPointByCanvas(brushMaxXCanvas, brushMaxYCanvas);

    for (const g6Node of this.g6.getNodes()) {
      const {
        minX: nodeMinX,
        minY: nodeMinY,
        maxX: nodeMaxX,
        maxY: nodeMaxY,
        centerX: nodeCenterX,
        centerY: nodeCenterY,
      } = g6Node.getBBox();

      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        brushMinX < nodeCenterX &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        nodeCenterX < brushMaxX &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        brushMinY < nodeCenterY &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        nodeCenterY < brushMaxY
      ) {
        if (!this.selecteeTargets[g6Node.getID()]) {
          this.selecteeTargets[g6Node.getID()] = this.g6.get('canvas').addShape('rect', {
            attrs: {
              stroke: Color.CorePurple500,
              lineWidth: 2,
              radius: 10,
              fill: 'transparent',
            },
            capture: false,
            name: 'selectee-rect',
          });
          const { x: canvasMinX, y: canvasMinY } = this.getCanvasByPoint(nodeMinX, nodeMinY);
          const { x: canvasMaxX, y: canvasMaxY } = this.getCanvasByPoint(nodeMaxX, nodeMaxY);
          const posAttr = {
            width: canvasMaxX - canvasMinX,
            height: canvasMaxY - canvasMinY,
            x: canvasMinX,
            y: canvasMinY,
          };
          this.selecteeTargets[g6Node.getID()].attr(posAttr);
        }
      } else {
        if (this.selecteeTargets[g6Node.getID()]) {
          this.selecteeTargets[g6Node.getID()]?.remove(true);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          this.selecteeTargets[g6Node.getID()] = null;
        }
      }
    }
  });

  brushEnd() {
    if (this.brushRect) {
      this.brushRect.remove(true);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.brushRect = null;
    }

    for (const nodeId in this.selecteeTargets) {
      this.selecteeTargets[nodeId]?.remove(true);
      delete this.selecteeTargets[nodeId];
    }
  }
  // ---------------------------------------------------------------------------------------
  // Selection box
  // ---------------------------------------------------------------------------------------
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  public selectionBox: G6.IShape;

  selectionBoxDestroy() {
    this.selectionBox?.remove(true);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.selectionBox = null;
  }

  selectionBoxUpdate(canvasWidth: number, canvasHeight: number, canvasX: number, canvasY: number) {
    if (!this.selectionBox) {
      this.selectionBox = this.g6.get('canvas').addShape('rect', {
        attrs: {
          stroke: Color.CorePurple700,
          lineWidth: 1,
          fill: 'transparent',
        },
        capture: false,
        name: 'brush-shape',
      });
    }
    const posAttr = {
      width: canvasWidth + 8,
      height: canvasHeight + 8,
      x: canvasX - 4,
      y: canvasY - 4,
    };
    this.selectionBox.attr(posAttr);
  }

  selectionBoxUpdateByCanvasSelectables(selectables: CanvasSelectable[]) {
    if (selectables.length === 0) {
      this.selectionBoxDestroy();
    } else {
      const { minX, minY, maxX, maxY } = selectables.reduce(
        (
          memo: { minX?: number; minY?: number; maxX?: number; maxY?: number },
          selectables: CanvasSelectable,
        ) => {
          const { minX, minY, maxX, maxY } = this.nodeBBoxBySelectable(selectables);

          return {
            minX: memo.minX ? Math.min(minX, memo.minX) : minX,
            minY: memo.minY ? Math.min(minY, memo.minY) : minY,
            maxX: memo.maxX ? Math.max(maxX, memo.maxX) : maxX,
            maxY: memo.maxY ? Math.max(maxY, memo.maxY) : maxY,
          };
        },
        {},
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { x: canvasMinX, y: canvasMinY } = this.getCanvasByPoint(minX, minY);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { x: canvasMaxX, y: canvasMaxY } = this.getCanvasByPoint(maxX, maxY);

      this.selectionBoxUpdate(
        canvasMaxX - canvasMinX,
        canvasMaxY - canvasMinY,
        canvasMinX,
        canvasMinY,
      );
    }
  }

  // ---------------------------------------------------------------------------------------
  // Collapsed Groups
  // ---------------------------------------------------------------------------------------
  calculateRelativeNodePositions(nodeIds: NodeId[], collapsedGroups: CollapsedGroup[]) {
    const absoluteNodePositions: Map<HashableNodeId, NodePosition> = new Map();
    for (const nodeId of nodeIds) {
      const g6Node = this.nodeByNodeId(nodeId);
      const bbox = g6Node.getCanvasBBox();
      absoluteNodePositions.set(nodeIdToHashable(nodeId), { x: bbox.x, y: bbox.y });
    }

    for (const collapsedGroup of collapsedGroups) {
      const collapsedGroupId = collapsedGroup.groupId;
      const g6CollapsedGroup = this.nodeByCollapsedGroupId(collapsedGroupId);
      const collapsedGroupAbsolutePosition = g6CollapsedGroup.getCanvasBBox();

      for (const [member, relativeNodePos] of collapsedGroup.relativeNodePositions) {
        // calculate the new absolute position of nodes from the collapsed group
        const { x: deltaX, y: deltaY } = relativeNodePos;

        absoluteNodePositions.set(member, {
          x: collapsedGroupAbsolutePosition.x + deltaX,
          y: collapsedGroupAbsolutePosition.y + deltaY,
        });
      }
    }

    // Find the bbox that surrounds all nodes in the collapsed group. Recalculate the position
    // of all nodes relative to the center of that bbox
    let maxX;
    let minX;
    let maxY;
    let minY;
    for (const { x, y } of absoluteNodePositions.values()) {
      maxX = maxX ? Math.max(x, maxX) : x;
      minX = minX ? Math.min(x, minX) : x;
      maxY = maxY ? Math.max(y, maxY) : y;
      minY = minY ? Math.min(y, minY) : y;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const centerX = (maxX + minX) / 2;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const centerY = (maxY + minY) / 2;

    const relativeNodePositions = new Map();
    for (const [nodeId, { x: absX, y: absY }] of absoluteNodePositions) {
      relativeNodePositions.set(nodeId, { x: absX - centerX, y: absY - centerY });
    }
    return relativeNodePositions;
  }

  positionNewCollapsedGroup(
    selectedNodeIds: Set<HashableNodeId>,
    selectedCollapsedGroupIds: Set<CollapsedGroupId>,
  ): NodePosition {
    const selectedG6Ids = new Set([
      ...[...selectedNodeIds].map(hashableNodeIdToG6Id),
      ...[...selectedCollapsedGroupIds].map(collapsedGroupIdToG6Id),
    ]);

    // start at the center of the selection box.
    let startX = 0;
    let startY = 0;
    if (this.selectionBox) {
      const bbox = this.selectionBox.getBBox();
      const { x: centerX, y: centerY } = this.getPointByCanvas(
        (bbox.maxX + bbox.minX) / 2,
        (bbox.maxY + bbox.minY) / 2,
      );
      startX = centerX;
      startY = centerY;
    }

    // slowly move away from the starting position until you found a place for the collapsed group
    // that does not overlap with any node/collapsed group that persists after the collapse
    let r = 0;
    let newX: number;
    let newY: number;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const rad = Math.random() * 10;
      newX = startX + Math.cos(rad) * r;
      newY = startY + Math.sin(rad) * r;

      const overlappingG6Nodes = this.getNodesInBox(
        newX - 80, // arbitrary padding from the center of the collapsedGroup
        newY - 80,
        newX + 80,
        newY + 80,
      );

      // filter out nodes that will be removed in this action
      const persistingG6Nodes = overlappingG6Nodes.filter((g6Node) => {
        const id = g6Node.getID();
        return !selectedG6Ids.has(id);
      });
      if (persistingG6Nodes.length === 0) {
        break;
      }

      r += 20;
    }

    return {
      x: newX,
      y: newY,
    };
  }
}

export default G6Wrapper;
